// The Wildwood: the south-east section, a rainforest below the High Peaks.
// Giant buttress-rooted trees with vines, tree ferns and blooming bushes over
// a floor of moss and leaf litter; the river from the Peaks drops over a mossy
// ledge into a pool and winds on west toward the Dunes. Where it meets the
// Peaks (north) and the Dunes (west) the forest edge is ragged, so the lands
// run into each other. No places yet: land to wander.
//
// Its own random streams (seed + 900 and up), consumed in full whatever region
// is being painted.
import { rng, noise2, softShadow, wash, blobPath, inkPoly, inkEllipse, line, withAlpha, mix, hatch, inkedCircles, clipCircles } from "./paint"
import { paintTuft, paintBush, paintRock, paintLog } from "./nature"

export function buildWildwood(config, { x0, y0, x1, y1 }) {
    const seed = config.seed
    const active = !!config.sections?.some((s) => s.key === "wildwood")
    const n = noise2(seed + 900), n2 = noise2(seed + 901)
    const w = x1 - x0, h = y1 - y0
    const falls = active ? config.falls : null
    const pool = falls?.pool || null
    const inPool = (x, y, pad = 0) => !!pool && ((x - pool.x) / (pool.rx + pad)) ** 2 + ((y - pool.y) / (pool.ry + pad)) ** 2 < 1
    // the ragged edges where the forest meets the Peaks and the Dunes
    const topEdge = (x) => y0 + 60 + (n(x / 900, 3.3) - 0.5) * 520 + (n(x / 210, 8.7) - 0.5) * 140
    const leftEdge = (y) => x0 + 40 + (n(7.1, y / 900) - 0.5) * 620 + (n(2.9, y / 210) - 0.5) * 160
    const inForest = (x, y) => active && x >= leftEdge(y) && y >= topEdge(x) && x < x1 + 40 && y < y1 + 40
    const edgePoly = () => {
        const pts = []
        for (let x = x1 + 40; x >= x0 - 200; x -= 24) pts.push([Math.max(x, leftEdge(topEdge(x))), topEdge(x)])
        for (let y = y0 - 200; y <= y1 + 40; y += 24) pts.push([leftEdge(y), Math.max(y, topEdge(leftEdge(y)))])
        pts.push([x1 + 40, y1 + 40])
        return pts
    }
    const poly = active ? edgePoly() : []

    function biome(x, y) {
        return inForest(x, y) ? "rainforest" : null
    }

    // --- the land -----------------------------------------------------------------

    function paintLand(ctx, pal, vis) {
        if (!active) return
        const area = (w * h) / (2000 * 1250)
        ctx.save()
        ctx.beginPath(); poly.forEach(([x, y], i) => (i ? ctx.lineTo(x, y) : ctx.moveTo(x, y))); ctx.closePath()
        // a darker lip of undergrowth along the forest edge
        ctx.strokeStyle = withAlpha(pal.rainDark, 0.6); ctx.lineWidth = 14; ctx.lineJoin = "round"; ctx.stroke()
        ctx.fillStyle = pal.rain; ctx.fill()
        ctx.clip()
        const r = rng(seed + 902)
        for (let i = 0; i < 90 * area; i++) {
            const x = x0 + r() * w, y = y0 + r() * h, rad = 80 + r() * 230, a = 0.35 + r() * 0.3
            if (!vis(x, y, rad)) continue
            const v = n2(x / 280, y / 280)
            wash(ctx, x, y, rad, v > 0.6 ? pal.rainLight : v > 0.38 ? pal.rainDark : pal.canopyShadow, v < 0.38 ? a * 0.5 : a)
        }
        // leaf litter and moss
        for (let i = 0; i < 9000 * area; i++) {
            const x = x0 + r() * w, y = y0 + r() * h, kind = r()
            if (!vis(x, y, 4)) continue
            const v = n2(x / 40, y / 40)
            ctx.fillStyle = kind < 0.04 ? withAlpha(pal.bark, 0.5) : v > 0.6 ? withAlpha(pal.rainLight, 0.55) : v < 0.35 ? withAlpha(pal.rainDark, 0.5) : withAlpha(pal.fern, 0.3)
            ctx.beginPath(); ctx.ellipse(x, y, 2.6, 1.4, kind * 3, 0, Math.PI * 2); ctx.fill()
        }
        // the world's east and south edges darken into deep forest
        const edge = 180
        ;[[x1, 0, x1 - edge, 0, x1 - edge, y0, edge, h + 40], [0, y1, 0, y1 - edge, x0 - 200, y1 - edge, w + 240, edge]].forEach(([gx0, gy0, gx1, gy1, rx, ry, rw, rh]) => {
            const g = ctx.createLinearGradient(gx0, gy0, gx1, gy1)
            g.addColorStop(0, withAlpha(pal.canopyShadow, 0.8)); g.addColorStop(1, withAlpha(pal.canopyShadow, 0))
            ctx.fillStyle = g; ctx.fillRect(rx, ry, rw, rh)
        })
        ctx.restore()
        // where the forest meets the Peaks' meadows and the Dunes' sand, a band
        // of scrubby green bleeds out a little way
        const rs = rng(seed + 903)
        for (let x = x0 - 200; x < x1 + 40; x += 70) {
            const y = topEdge(x), rad = 50 + rs() * 60
            if (!vis(x, y, rad + 20)) continue
            wash(ctx, x, y - 20, rad, pal.rain, 0.3)
        }
        for (let y = y0 - 200; y < y1 + 40; y += 70) {
            const x = leftEdge(y), rad = 50 + rs() * 60
            if (!vis(x, y, rad + 20)) continue
            wash(ctx, x - 20, y, rad, pal.scrub, 0.35)
        }
    }

    // The falls: a mossy ledge across the river, a white curtain of water, and
    // the pool below, painted over the river itself.
    function paintOverRiver(ctx, pal, vis) {
        if (!falls || !vis(falls.x, falls.y, 260)) return
        const r = rng(seed + 904)
        // the pool
        if (pool) {
            blobPath(ctx, pool.x, pool.y + 4, pool.rx + 26, pool.ry + 18, seed + 905, 0.1, 30)
            ctx.fillStyle = withAlpha(pal.stoneDark, 0.8); ctx.fill()
            blobPath(ctx, pool.x, pool.y, pool.rx, pool.ry, seed + 906, 0.07, 30)
            const g = ctx.createRadialGradient(pool.x, pool.y - pool.ry * 0.3, 6, pool.x, pool.y, pool.rx)
            g.addColorStop(0, mix(pal.water, pal.foam, 0.5)); g.addColorStop(0.35, pal.water); g.addColorStop(1, pal.waterDeep)
            ctx.fillStyle = g; ctx.fill()
            ctx.strokeStyle = pal.ink; ctx.lineWidth = 1.6; ctx.stroke()
            // foam where the water lands
            for (let i = 0; i < 16; i++) inkEllipse(ctx, pool.x + (r() - 0.5) * 70, pool.y - pool.ry * 0.55 + r() * 30, 6 + r() * 9, 3 + r() * 3, withAlpha(pal.foam, 0.85), null)
        }
        // the ledge: mossy rocks across the river's course
        const lx = falls.x, ly = falls.y
        for (let i = 0; i < 9; i++) {
            const x = lx - 110 + i * 27 + (r() - 0.5) * 8, y = ly + (r() - 0.5) * 8, s = 14 + r() * 10
            inkEllipse(ctx, x, y, s, s * 0.6, pal.stone, pal.ink, 1.2)
            ctx.fillStyle = withAlpha(pal.moss, 0.9); ctx.beginPath(); ctx.ellipse(x - s * 0.2, y - s * 0.3, s * 0.6, s * 0.25, 0, 0, Math.PI * 2); ctx.fill()
        }
        // the curtain: streaks of white falling from the ledge to the pool
        const drop = pool ? pool.y - pool.ry * 0.5 - ly : 120
        for (let i = 0; i < 14; i++) {
            const x = lx - 34 + i * 5 + (r() - 0.5) * 2
            line(ctx, [[x, ly + 6], [x + (r() - 0.5) * 4, ly + drop]], withAlpha(i % 3 ? pal.foam : pal.waterLight, 0.9), 2.2)
        }
    }

    // --- nature ------------------------------------------------------------------------

    // `keep` clears what stands where something newer came (the railway),
    // after growing, so nothing else moves.
    function grow({ clearOf, spaced, note, inSea, keep = () => true }) {
        const sprites = [], footprints = [], trees = [], smalls = []
        if (!active) return { sprites, footprints, trees, smalls }
        const r = rng(seed + 920)
        const grove = noise2(seed + 921)
        // giants first, then the understorey between them
        const target = Math.round(170 * (w * h) / (2000 * 1250) * 1.1)
        for (let i = 0; i < target * 10 && trees.length < target; i++) {
            const x = x0 + 40 + r() * (w - 80), y = y0 + 40 + r() * (h - 80), g = grove(x / 300, y / 300), roll = r(), size0 = r(), sd = Math.floor(r() * 1e6)
            if (!inForest(x, y) || g < 0.16 || inPool(x, y, 60) || inSea(x, y, 90)) continue
            const kind = roll < 0.32 ? "giant" : roll < 0.74 ? "tree" : roll < 0.9 ? "fern" : "palm"
            const s = kind === "giant" ? 30 + size0 * 16 : kind === "tree" ? 20 + size0 * 12 : kind === "fern" ? 14 + size0 * 6 : 16 + size0 * 6
            if (!clearOf(x, y, s * 0.6) || !spaced(x, y, s * (kind === "fern" ? 0.7 : 1))) continue
            const t = { x, y, s, kind, seed: sd }
            trees.push(t); note(t)
        }
        for (let i = trees.length - 1; i >= 0; i--) if (!keep(trees[i])) trees.splice(i, 1)
        trees.forEach((t) => {
            if (t.kind === "fern") {
                sprites.push({ x: t.x, y: t.y, box: [-t.s * 1.8, -t.s * 3.2, t.s * 1.8, 6], kind: "tree", sway: 0.9, seed: t.seed, paint(ctx, pal) { paintTreeFern(ctx, pal, t.s, t.seed) } })
            } else {
                const giant = t.kind === "giant"
                sprites.push({ x: t.x, y: t.y, box: [-t.s * 1.5, -t.s * (giant ? 3.7 : 3.1), t.s * 1.5, 8], kind: "tree", sway: giant ? 0.4 : 0.8, seed: t.seed, paint(ctx, pal) { paintJungleTree(ctx, pal, t.s, t.seed, giant) } })
            }
            footprints.push({ circle: true, x: t.x, y: t.y - 2, r: t.s * (t.kind === "giant" ? 0.4 : 0.25) + 4 })
        })
        // blooming bushes and mossy boulders
        for (let i = 0, made = 0; i < 4000 && made < Math.round(60 * (w * h) / (2000 * 1250) * 0.5); i++) {
            const x = x0 + 40 + r() * (w - 80), y = y0 + 40 + r() * (h - 80), s = 9 + r() * 8, sd = Math.floor(r() * 1e6), rock = r() < 0.25
            if (!inForest(x, y) || inPool(x, y, 30) || inSea(x, y, 90) || !clearOf(x, y, 6) || !spaced(x, y, s)) continue
            const o = { x, y, s, rock, seed: sd }
            note(o); made++
            if (!keep(o)) continue
            smalls.push(o)
            if (rock) {
                sprites.push({ x, y, box: [-s * 1.4, -s * 1.3, s * 1.4, s * 0.8 + 4], kind: "rock", sway: 0, paint(ctx, pal) { paintRock(ctx, mossyPal(pal), s, sd) } })
                footprints.push({ circle: true, x, y: y - 2, r: s * 0.7 })
            } else sprites.push({ x, y, box: [-s * 1.7, -s * 2.2, s * 1.7, s * 0.8 + 4], kind: "bush", sway: 0.4, paint(ctx, pal) { paintBush(ctx, bloomPal(pal), s, sd) } })
        }
        // the understorey, grown last from its own stream so nothing above
        // moves: banana and monstera leaves, heliconias, mossy logs, fungi
        const ru = rng(seed + 940)
        const under = Math.round(90 * (w * h) / (2000 * 1250))
        for (let i = 0, made = 0; i < under * 12 && made < under; i++) {
            const x = x0 + 40 + ru() * (w - 80), y = y0 + 40 + ru() * (h - 80), roll = ru(), s0 = ru(), sd = Math.floor(ru() * 1e6)
            const kind = roll < 0.45 ? "banana" : roll < 0.7 ? "heliconia" : roll < 0.85 ? "fungi" : "log"
            const s = kind === "banana" ? 18 + s0 * 12 : kind === "heliconia" ? 15 + s0 * 7 : kind === "log" ? 20 + s0 * 8 : 10 + s0 * 5
            if (!inForest(x, y) || inPool(x, y, 40) || inSea(x, y, 90) || !clearOf(x, y, s * 0.5) || !spaced(x, y, s * 0.8)) continue
            const o = { x, y, s, kind, seed: sd }
            note(o); made++
            if (!keep(o)) continue
            smalls.push(o)
            if (kind === "banana") sprites.push({ x, y, box: [-s * 2.1, -s * 2.3, s * 2.1, 6], kind: "bush", sway: 0.5, paint(ctx, pal) { paintBanana(ctx, pal, s, sd) } })
            else if (kind === "heliconia") sprites.push({ x, y, box: [-s * 1.7, -s * 2.6, s * 1.7, 6], kind: "bush", sway: 0.6, paint(ctx, pal) { paintHeliconia(ctx, pal, s, sd) } })
            else if (kind === "fungi") {
                sprites.push({ x, y, box: [-s * 1.4, -s * 1.7, s * 1.4, 6], kind: "rock", sway: 0, paint(ctx, pal) { paintStump(ctx, pal, s) } })
                footprints.push({ circle: true, x, y: y - 2, r: s * 0.55 })
            } else {
                const angle = ((sd % 7) / 7 - 0.5) * 0.9
                sprites.push({ x, y, box: [-s * 1.7, -s * 1.2, s * 1.7, s * 0.9], kind: "log", sway: 0, paint(ctx, pal) { paintMossyLog(ctx, pal, s, angle, sd) } })
                footprints.push({ circle: true, x, y: y - 2, r: s * 0.6 })
            }
        }
        return { sprites, footprints, trees, smalls }
    }

    function paintShadows(ctx, pal, vis, grown) {
        grown.trees.forEach((t) => { if (vis(t.x, t.y, t.s * 3)) softShadow(ctx, t.x + t.s * 0.5, t.y + 3, t.s * 1.4, t.s * 0.5, pal.shadowAlpha * 1.2, pal.shadow) })
        grown.smalls.forEach((o) => { if (vis(o.x, o.y, o.s * 2)) softShadow(ctx, o.x + o.s * 0.3, o.y + 1, o.s * 1.2, o.s * 0.45, pal.shadowAlpha, pal.shadow) })
    }

    // Ferns and flowers on the forest floor.
    function paintDetails(ctx, pal, vis, clearOf, inSea) {
        if (!active) return
        const area = (w * h) / (2000 * 1250)
        // ground cover: low patches of broad leaves, so the floor isn't lawn
        // (its own stream, drawn first, under the ferns and flowers)
        const rg = rng(seed + 908)
        for (let i = 0; i < 380 * area; i++) {
            const x = x0 + rg() * w, y = y0 + rg() * h, n0 = 5 + Math.floor(rg() * 6), sd = Math.floor(rg() * 1e6), rad = 14 + rg() * 16
            if (!vis(x, y, rad + 12) || !inForest(x, y) || inPool(x, y, 30) || inSea(x, y, 60) || !clearOf(x, y, -16)) continue
            groundCover(ctx, pal, x, y, rad, n0, sd)
        }
        const r = rng(seed + 907)
        for (let i = 0; i < 260 * area; i++) {
            const x = x0 + r() * w, y = y0 + r() * h, kind = r(), sd = Math.floor(r() * 1e6)
            if (!vis(x, y, 20) || !inForest(x, y) || inPool(x, y, 20) || inSea(x, y, 60) || !clearOf(x, y, -12)) continue
            if (kind < 0.7) groundFern(ctx, pal, x, y, sd)
            else bloomClump(ctx, pal, x, y, sd)
        }
        ctx.lineWidth = 1.1
        for (let i = 0; i < 2000 * area; i++) {
            const x = x0 + r() * w, y = y0 + r() * h, ht = 4 + r() * 5
            if (!vis(x, y, 8) || !inForest(x, y) || inPool(x, y, 8) || inSea(x, y, 60) || !clearOf(x, y, -14)) continue
            ctx.globalAlpha = 0.45
            paintTuft(ctx, n2(x / 50, y / 50) > 0.5 ? pal.rainLight : pal.rainDark, x, y, ht)
        }
        ctx.globalAlpha = 1
    }

    return { active, inPool, inWater: (x, y, pad = 0) => inPool(x, y, pad), biome, inForest, falls, paintLand, paintOverRiver, grow, paintShadows, paintDetails }
}

// ---- painters -------------------------------------------------------------------------

const mossyCache = new Map()
function mossyPal(pal) {
    if (!mossyCache.has(pal)) mossyCache.set(pal, { ...pal, moss: pal.rainLight })
    return mossyCache.get(pal)
}
const bloomCache = new Map()
function bloomPal(pal) {
    if (!bloomCache.has(pal)) bloomCache.set(pal, { ...pal, leaf: pal.canopy, leafDark: pal.canopyDark, leafLight: pal.canopyLight, berry: pal.bloom, flowers: [pal.bloom, pal.bloom2, "#EE9AA8", "#B7A2E6"] })
    return bloomCache.get(pal)
}

// A rainforest tree: a tall trunk flaring into buttress roots, a broad crown
// of deep-green lobes hatched in shadow, and vines hanging from it. Giants
// are taller, wider and darker.
export function paintJungleTree(ctx, pal, s, seed, giant) {
    const r = rng(seed)
    const ink = pal.ink
    const tw = s * 0.16, trunkTop = -s * (giant ? 2.0 : 1.5)
    // buttress roots
    ;[-1, 1].forEach((side) => inkPoly(ctx, [[side * tw * 0.6, trunkTop * 0.3], [side * tw * 3.2, 0], [side * tw * 1.2, 0]], mix(pal.bark, "#000000", 0.1), ink, { line: 1.3 }))
    inkPoly(ctx, [[-tw * 1.3, 0], [-tw * 0.8, trunkTop * 0.3], [-tw * 0.6, trunkTop], [tw * 0.6, trunkTop], [tw * 0.8, trunkTop * 0.3], [tw * 1.3, 0]], pal.bark, ink, { line: 1.5 })
    line(ctx, [[tw * 0.2, -s * 0.2], [tw * 0.3, trunkTop * 0.9]], mix(pal.bark, "#000000", 0.3), 1.4, 0.8)
    const cy = trunkTop - s * 0.45
    const lobes = []
    const count = (giant ? 9 : 7) + Math.floor(r() * 3)
    for (let i = 0; i < count; i++) {
        const a = (i / count) * Math.PI * 2 + r() * 0.5, d = s * (0.5 + r() * 0.22)
        lobes.push([Math.cos(a) * d * 1.25, cy + Math.sin(a) * d * 0.62, s * (0.42 + r() * 0.18)])
    }
    lobes.push([0, cy - s * 0.15, s * 0.62])
    // not every crown the same green: some bluer, some yellower, a few in flower
    const variant = seed % 9
    const base = giant ? pal.canopyDark : variant < 2 ? mix(pal.canopy, pal.lagoonDeep, 0.2) : variant < 4 ? mix(pal.canopy, pal.palmLeafLight, 0.35) : pal.canopy
    inkedCircles(ctx, lobes, mix(base, "#000000", 0.2), ink, 1.9)
    ctx.save()
    clipCircles(ctx, lobes)
    lobes.forEach(([x, y, rad]) => { ctx.fillStyle = base; ctx.beginPath(); ctx.arc(x - rad * 0.16, y - rad * 0.22, rad * 0.82, 0, Math.PI * 2); ctx.fill() })
    lobes.forEach(([x, y, rad]) => { if (x < s * 0.2) { ctx.fillStyle = pal.canopyLight; ctx.beginPath(); ctx.arc(x - rad * 0.32, y - rad * 0.4, rad * 0.38, 0, Math.PI * 2); ctx.fill() } })
    for (let i = 0; i < s * 3; i++) {
        const x = (r() - 0.5) * s * 2.6, y = cy + (r() - 0.55) * s * 1.4
        ctx.strokeStyle = x + (y - cy) < 0 ? withAlpha(pal.canopyLight, 0.7) : withAlpha(pal.canopyShadow, 0.7)
        ctx.lineWidth = 1.1
        ctx.beginPath(); ctx.arc(x, y, 2.4 + r() * 2, Math.PI * 0.1, Math.PI * 0.9); ctx.stroke()
    }
    ctx.beginPath(); ctx.ellipse(s * 0.55, cy + s * 0.35, s * 1.2, s * 0.6, -0.4, 0, Math.PI * 2); ctx.clip()
    hatch(ctx, [-s * 1.5, cy - s * 1.2, s * 3, s * 2.4], pal.canopyShadow, { seed, gap: 2.6, alpha: 0.6, width: 1.1 })
    ctx.restore()
    if (!giant && variant === 8) {
        // a flowering tree: blossom scattered over the lit side of the crown
        const rb = rng(seed + 1)
        ctx.save(); clipCircles(ctx, lobes)
        for (let i = 0; i < s * 1.1; i++) {
            const bx = (rb() - 0.62) * s * 2.2, by = cy + (rb() - 0.65) * s * 1.2
            ctx.fillStyle = rb() < 0.6 ? mix(pal.bloom, pal.flowers[2], 0.45) : pal.bloom2
            ctx.beginPath(); ctx.arc(bx, by, 1.3 + rb() * 1.3, 0, Math.PI * 2); ctx.fill()
        }
        ctx.restore()
    }
    // vines hanging from the crown
    for (let i = 0; i < (giant ? 5 : 3); i++) {
        const x = (r() - 0.5) * s * 1.8, y0 = cy + s * 0.3, len = s * (0.5 + r() * 0.8)
        ctx.strokeStyle = pal.fern; ctx.lineWidth = 1.4
        ctx.beginPath(); ctx.moveTo(x, y0); ctx.quadraticCurveTo(x + (r() - 0.5) * 10, y0 + len * 0.5, x + (r() - 0.5) * 6, y0 + len); ctx.stroke()
        ctx.fillStyle = pal.fern; ctx.beginPath(); ctx.ellipse(x, y0 + len, 2.5, 1.6, 0.5, 0, Math.PI * 2); ctx.fill()
    }
}

// A tree fern: a slim dark stem and a spray of arching fronds.
export function paintTreeFern(ctx, pal, s, seed) {
    const r = rng(seed)
    const top = -s * 2.1
    line(ctx, [[0, 0], [(r() - 0.5) * 6, top]], pal.ink, 4); line(ctx, [[0, 0], [(r() - 0.5) * 6, top]], pal.bark, 2.6)
    const fronds = 9
    for (let i = 0; i < fronds; i++) {
        const a = Math.PI + (i / (fronds - 1)) * Math.PI + (r() - 0.5) * 0.2
        const len = s * (1.1 + r() * 0.4), ex = Math.cos(a) * len, ey = top + Math.sin(a) * len * 0.5 + s * 0.6
        ctx.strokeStyle = pal.ink; ctx.lineWidth = 2.8
        ctx.beginPath(); ctx.moveTo(0, top); ctx.quadraticCurveTo(ex * 0.5, top - s * 0.5, ex, ey); ctx.stroke()
        ctx.strokeStyle = i % 2 ? pal.fern : pal.canopyLight; ctx.lineWidth = 1.8
        ctx.beginPath(); ctx.moveTo(0, top); ctx.quadraticCurveTo(ex * 0.5, top - s * 0.5, ex, ey); ctx.stroke()
        for (let k = 1; k < 8; k++) {
            const t = k / 8, px = 2 * (1 - t) * t * ex * 0.5 + t * t * ex, py = (1 - t) * (1 - t) * top + 2 * (1 - t) * t * (top - s * 0.5) + t * t * ey
            line(ctx, [[px, py], [px + 3, py + 5 * (1 - t) + 2]], pal.fern, 1.2)
            line(ctx, [[px, py], [px - 3, py + 5 * (1 - t) + 2]], pal.canopyLight, 1.2)
        }
    }
}

// A lens-shaped leaf from (bx, by) to (tx, ty), `w` wide, with a pale midrib.
function bigLeaf(ctx, pal, bx, by, tx, ty, w, fill, torn) {
    const mx = (bx + tx) / 2, my = (by + ty) / 2, dx = tx - bx, dy = ty - by, len = Math.hypot(dx, dy) || 1
    const px = (-dy / len) * w, py = (dx / len) * w
    ctx.beginPath(); ctx.moveTo(bx, by)
    ctx.quadraticCurveTo(mx + px, my + py, tx, ty)
    ctx.quadraticCurveTo(mx - px, my - py, bx, by)
    ctx.fillStyle = fill; ctx.fill(); ctx.strokeStyle = pal.ink; ctx.lineWidth = 1.3; ctx.stroke()
    line(ctx, [[bx, by], [tx, ty]], pal.rainLight, 1.2, 0.9)
    if (torn) line(ctx, [[mx + px * 0.55, my + py * 0.55], [mx + px * 0.15 + dx * 0.05, my + py * 0.15 + dy * 0.05]], pal.ink, 1, 0.8)
}

// A clump of banana and monstera leaves: big paddles arching out and
// drooping, the ones pointing up behind the rest.
function paintBanana(ctx, pal, s, seed) {
    const r = rng(seed)
    const n = 5 + Math.floor(r() * 3)
    const leaves = Array.from({ length: n }, (_, i) => {
        const a = -Math.PI - 0.25 + (i / (n - 1)) * (Math.PI + 0.5) + (r() - 0.5) * 0.25
        return { a, len: s * (1.35 + r() * 0.5), shade: r(), torn: r() < 0.5 }
    }).sort((p, q) => Math.sin(p.a) - Math.sin(q.a))
    const bx = 0, by = -s * 0.25
    leaves.forEach(({ a, len, shade, torn }) => {
        const tx = bx + Math.cos(a) * len, ty = by + Math.sin(a) * len * 0.75 + len * 0.3 * (1 - Math.abs(Math.sin(a)))
        line(ctx, [[bx, 0], [bx + Math.cos(a) * len * 0.25, by + Math.sin(a) * len * 0.2]], pal.ink, 2.2)
        bigLeaf(ctx, pal, bx + Math.cos(a) * len * 0.22, by + Math.sin(a) * len * 0.18, tx, ty, s * 0.62, shade < 0.33 ? pal.palmLeafLight : shade < 0.66 ? pal.canopyLight : mix(pal.canopyLight, pal.palmLeafLight, 0.5), torn)
    })
}

// A heliconia: upright stems hung with zigzag red-and-gold bracts over two
// long leaves.
function paintHeliconia(ctx, pal, s, seed) {
    const r = rng(seed)
    bigLeaf(ctx, pal, 0, -2, -s * 1.3, -s * 1.5, s * 0.42, pal.canopyLight, false)
    bigLeaf(ctx, pal, 0, -2, s * 1.35, -s * 1.3, s * 0.4, pal.palmLeafLight, r() < 0.5)
    for (let i = 0; i < 3; i++) {
        const x = (i - 1) * s * 0.38 + (r() - 0.5) * 3, top = -s * (1.6 + r() * 0.6)
        line(ctx, [[x, 0], [x, top]], pal.ink, 2.4)
        line(ctx, [[x, 0], [x, top]], pal.fern, 1.2)
        for (let k = 0; k < 5; k++) {
            const y = top + k * s * 0.2, side = k % 2 ? 1 : -1
            inkPoly(ctx, [[x, y], [x + side * s * 0.38, y + s * 0.02], [x, y + s * 0.2]], k % 2 ? pal.bloom : pal.reef2, pal.ink, { line: 1 })
        }
    }
}

// An old stump grown over with moss, bracket fungi stepping up its side.
function paintStump(ctx, pal, s) {
    inkPoly(ctx, [[-s * 0.55, 0], [-s * 0.45, -s * 1.1], [s * 0.45, -s * 1.1], [s * 0.55, 0]], pal.bark, pal.ink, { line: 1.5 })
    inkEllipse(ctx, 0, -s * 1.1, s * 0.45, s * 0.16, mix(pal.bark, pal.woodLight, 0.5), pal.ink, 1.2)
    ctx.strokeStyle = withAlpha(pal.ink, 0.35); ctx.lineWidth = 0.8
    ctx.beginPath(); ctx.ellipse(0, -s * 1.1, s * 0.25, s * 0.08, 0, 0, Math.PI * 2); ctx.stroke()
    ctx.fillStyle = withAlpha(pal.rainLight, 0.9); ctx.beginPath(); ctx.ellipse(-s * 0.2, -s * 0.25, s * 0.4, s * 0.18, 0.2, 0, Math.PI * 2); ctx.fill()
    for (let i = 0; i < 3; i++) {
        const side = i % 2 ? 1 : -1, y = -s * (0.35 + i * 0.25), x = side * s * 0.45
        ctx.beginPath(); ctx.ellipse(x + side * s * 0.15, y, s * (0.32 - i * 0.04), s * 0.13, 0, Math.PI, Math.PI * 2)
        ctx.closePath(); ctx.fillStyle = i === 1 ? pal.bloom2 : mix(pal.bloom2, pal.woodLight, 0.4); ctx.fill()
        ctx.strokeStyle = pal.ink; ctx.lineWidth = 1.1; ctx.stroke()
    }
}

// A fallen log, moss along its back and a fern at one end.
function paintMossyLog(ctx, pal, s, angle, seed) {
    const r = rng(seed)
    paintLog(ctx, pal, s, angle)
    ctx.save(); ctx.rotate(angle); ctx.scale(1, angle ? 0.8 : 1)
    for (let i = 0; i < 6; i++) {
        ctx.fillStyle = withAlpha(i % 2 ? pal.rainLight : pal.fern, 0.9)
        ctx.beginPath(); ctx.ellipse(-s * 0.8 + i * s * 0.3 + (r() - 0.5) * 4, -s * 0.26, s * 0.2, s * 0.08, 0, 0, Math.PI * 2); ctx.fill()
    }
    ctx.restore()
}

// A low patch of broad leaves lying on the forest floor.
function groundCover(ctx, pal, x, y, rad, count, seed) {
    const r = rng(seed)
    for (let i = 0; i < count; i++) {
        const a = r() * Math.PI * 2, d = r() * rad, lx = x + Math.cos(a) * d, ly = y + Math.sin(a) * d * 0.55, rot = r() * Math.PI, k = r()
        ctx.fillStyle = k < 0.4 ? pal.rainDark : k < 0.8 ? mix(pal.fern, pal.canopyLight, 0.4) : pal.canopyLight
        ctx.beginPath(); ctx.ellipse(lx, ly, 7 + r() * 5, 3 + r() * 2, rot, 0, Math.PI * 2); ctx.fill()
        ctx.strokeStyle = withAlpha(pal.canopyShadow, 0.5); ctx.lineWidth = 0.8; ctx.stroke()
    }
}

function groundFern(ctx, pal, x, y, seed) {
    const r = rng(seed)
    for (let i = 0; i < 5; i++) {
        const a = -Math.PI * 0.9 + (i / 4) * Math.PI * 0.8, len = 9 + r() * 6
        const ex = x + Math.cos(a) * len, ey = y + Math.sin(a) * len * 0.7
        line(ctx, [[x, y], [ex, ey]], i % 2 ? pal.fern : pal.rainLight, 1.4)
        for (let k = 1; k < 4; k++) { const px = x + (ex - x) * (k / 4), py = y + (ey - y) * (k / 4); line(ctx, [[px, py], [px + 2, py + 3]], pal.fern, 0.9) }
    }
}

function bloomClump(ctx, pal, x, y, seed) {
    const r = rng(seed)
    const color = r() < 0.5 ? pal.bloom : pal.bloom2
    for (let i = 0; i < 5; i++) {
        const fx = x + (r() - 0.5) * 16, fy = y + (r() - 0.5) * 8
        line(ctx, [[fx, fy + 5], [fx, fy]], pal.rainDark, 1)
        ctx.fillStyle = color
        for (let p = 0; p < 4; p++) { const a = (p / 4) * Math.PI * 2 + 0.4; ctx.beginPath(); ctx.ellipse(fx + Math.cos(a) * 2, fy + Math.sin(a) * 2, 1.9, 1.1, a, 0, Math.PI * 2); ctx.fill() }
    }
}
